"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Info, MapPin, ShieldCheck, Users } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { GeocodedAddressFields } from "@/src/components/address/geocoded-address-fields";
import type { GeocodedAddressFieldErrors } from "@/src/components/address/types";
import {
  FIELD_CLASSNAME,
  GENDER_OPTIONS,
  isMinorDateOfBirth,
  patientDetailsFormSchema,
  type PatientDetailsDraft,
} from "@/src/components/online-scheduling/types";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/src/components/shadcn/ui/form";
import { Input } from "@/src/components/shadcn/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/shadcn/ui/select";
import { onlineSchedulingGeocodingClient } from "@/src/lib/geocoding-client";
import { USState } from "@/src/lib/us-states";
import { formatPhoneNumberAsTyped } from "@/src/lib/utils/date-and-phone-utils";

function OptionalHint() {
  return <span className="ml-1 font-normal text-muted-foreground">(optional)</span>;
}

function mapHomeAddressErrors(error: unknown): GeocodedAddressFieldErrors {
  if (!error || typeof error !== "object") {
    return {};
  }

  const record = error as Record<string, { message?: string } | undefined>;

  return {
    line1: record.line1?.message,
    line2: record.line2?.message,
    city: record.city?.message,
    state: record.state?.message,
    postalCode: record.postalCode?.message,
  };
}

export function DetailsStep({
  formId,
  defaultValues,
  onSubmit,
}: {
  formId: string;
  defaultValues: PatientDetailsDraft;
  onSubmit: (values: PatientDetailsDraft) => void;
}) {
  const todayIso = new Date().toISOString().slice(0, 10);

  const form = useForm<PatientDetailsDraft>({
    resolver: zodResolver(patientDetailsFormSchema),
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  useEffect(() => {
    const subscription = form.watch((_value, { type }) => {
      if (type === "change") {
        form.clearErrors();
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const isMinor = isMinorDateOfBirth(form.watch("dateOfBirth"));

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(onSubmit)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
          }
        }}
        className="space-y-6"
        noValidate
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First name</FormLabel>
                <FormControl>
                  <Input autoComplete="given-name" placeholder="Jordan" className={FIELD_CLASSNAME} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last name</FormLabel>
                <FormControl>
                  <Input autoComplete="family-name" placeholder="Rivera" className={FIELD_CLASSNAME} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="dateOfBirth"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date of birth</FormLabel>
                <FormControl>
                  <Input type="date" max={todayIso} autoComplete="bday" className={FIELD_CLASSNAME} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mobile phone</FormLabel>
                <FormControl>
                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="555-123-4567"
                    className={FIELD_CLASSNAME}
                    {...field}
                    onChange={(event) => field.onChange(formatPhoneNumberAsTyped(event.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className={FIELD_CLASSNAME}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Gender</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className={FIELD_CLASSNAME}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {GENDER_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4 rounded-xl border border-dashed p-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-semibold">Home address</span>
          </div>
          <FormField
            control={form.control}
            name="homeAddress"
            render={({ field }) => (
              <FormItem>
                <GeocodedAddressFields
                  value={field.value}
                  onChange={field.onChange}
                  geocodingClient={onlineSchedulingGeocodingClient}
                  biasStateCode={USState.TN}
                  inputClassName={FIELD_CLASSNAME}
                  errors={mapHomeAddressErrors(form.formState.errors.homeAddress)}
                  idPrefix="home-address"
                />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4 rounded-xl border border-dashed p-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-semibold">
              Insurance
              <OptionalHint />
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="insurancePayer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Carrier</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Blue Cross Blue Shield" className={FIELD_CLASSNAME} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="insuranceMemberId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Member ID</FormLabel>
                  <FormControl>
                    <Input placeholder="X1234567890" className={FIELD_CLASSNAME} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {isMinor && (
          <div className="space-y-4 rounded-xl border border-dashed p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold">Parent or legal guardian</span>
            </div>
            <p className="flex items-start gap-2 text-sm text-muted-foreground" role="note">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              Patients under 18 must be accompanied by a parent or legal guardian at their appointment.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="guardianFirstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Guardian first name</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" placeholder="Alex" className={FIELD_CLASSNAME} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="guardianLastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Guardian last name</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" placeholder="Rivera" className={FIELD_CLASSNAME} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="guardianPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Guardian phone</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      inputMode="tel"
                      autoComplete="off"
                      placeholder="555-123-4567"
                      className={FIELD_CLASSNAME}
                      {...field}
                      onChange={(event) => field.onChange(formatPhoneNumberAsTyped(event.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </form>
    </Form>
  );
}
